import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { showAlert } from "../alertService";
import { Heading } from "./heading";
import { useAuth } from "./AuthProvider";

const FEEDBACK_STATUS_OPTIONS = [
  {
    value: "pending",
    label: "Pendiente",
    color: "bg-yellow-50 text-yellow-700 border border-yellow-200",
  },
  {
    value: "reviewed",
    label: "En revisión",
    color: "bg-blue-50 text-blue-700 border border-blue-200",
  },
  {
    value: "resolved",
    label: "Resuelto",
    color: "bg-green-50 text-green-700 border border-green-200",
  },
];

const FEEDBACK_STATUS_BADGE_CLASSES = {
  pending:
    "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-200",
  reviewed:
    "bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-200",
  resolved:
    "bg-green-100 text-green-800 hover:bg-green-200 border border-green-200",
};

function formatDateTime(ts) {
  if (!ts?.toDate) return "-";
  try {
    return ts.toDate().toLocaleString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "America/Mexico_City",
    });
  } catch {
    return "-";
  }
}

const FeedbackAdmin = () => {
  const { userInfo } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    if (userInfo?.role === "admin") {
      getDocs(collection(db, "feedback")).then((snapshot) => {
        setFeedbacks(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      });
    }
  }, [userInfo]);

  const handleStatusChange = async (id, newStatus) => {
    setUpdating(id);
    try {
      await updateDoc(doc(db, "feedback", id), {
        status: newStatus,
        updatedAt: new Date(),
      });
      showAlert("Estado actualizado correctamente", "success");
      // Refresh data
      const snapshot = await getDocs(collection(db, "feedback"));
      setFeedbacks(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      showAlert("Error actualizando estado", "error");
    }
    setUpdating(null);
  };

  if (userInfo?.role !== "admin") return null;

  return (
    <div className="p-4 space-y-8 max-w-5xl mx-auto">
      <Heading level={2} className="mb-6">
        Feedback de Usuarios
      </Heading>
      <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
        <table className="min-w-full table-auto divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Tema
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Feedback
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actualizado
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {feedbacks.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  No hay feedback para mostrar.
                </td>
              </tr>
            )}
            {feedbacks.map((fb, idx) => (
              <tr
                key={fb.id}
                className={
                  idx % 2 === 0
                    ? "even:bg-gray-50 hover:bg-gray-100"
                    : "hover:bg-gray-100"
                }
              >
                <td className="px-6 py-4 align-middle whitespace-nowrap text-sm text-gray-900">
                  {formatDateTime(fb.createdAt)}
                </td>
                <td className="px-6 py-4 align-middle whitespace-nowrap text-sm text-gray-900">
                  {fb.userEmail}
                </td>
                <td className="px-6 py-4 align-middle whitespace-nowrap text-sm text-gray-900">
                  {fb.topic}
                </td>
                <td className="px-6 py-4 align-middle whitespace-normal break-words text-sm text-gray-900 max-w-[320px]">
                  {fb.feedback}
                </td>
                <td className="px-6 py-4 align-middle whitespace-nowrap">
                  <label className="relative group cursor-pointer w-full">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors border flex items-center gap-1 w-full justify-between ${
                        FEEDBACK_STATUS_BADGE_CLASSES[fb.status || "pending"] ||
                        "bg-gray-100 text-gray-800 border-gray-200"
                      }`}
                    >
                      <span>
                        {
                          FEEDBACK_STATUS_OPTIONS.find(
                            (opt) => opt.value === (fb.status || "pending")
                          )?.label
                        }
                      </span>
                      <svg
                        className="w-3 h-3 ml-auto text-gray-400 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      <select
                        value={fb.status || "pending"}
                        onChange={(e) =>
                          handleStatusChange(fb.id, e.target.value)
                        }
                        disabled={updating === fb.id}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        style={{ appearance: "none" }}
                      >
                        {FEEDBACK_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  </label>
                </td>
                <td className="px-6 py-4 align-middle whitespace-nowrap text-sm text-gray-900">
                  {formatDateTime(fb.updatedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeedbackAdmin;
